/**
 * Generic in-memory cache with TTL expiration.
 *
 * @template T - The type of cached values
 */
export class TtlCache<T> {
    private cache = new Map<string, { data: T; expiresAt: number }>()
    private readonly ttlMs: number

    constructor (ttlMs: number) {
        this.ttlMs = ttlMs
    }

    /** Get a cached value, or undefined if missing/expired. */
    public get (key: string): T | undefined {
        const entry = this.cache.get(key)
        if (!entry) { return undefined }
        if (Date.now() >= entry.expiresAt) {
            this.cache.delete(key)
            return undefined
        }
        return entry.data
    }

    /** Store a value with the configured TTL. */
    public set (key: string, data: T): void {
        this.cache.set(key, { data, expiresAt: Date.now() + this.ttlMs })
    }

    /** Remove a specific key. */
    public delete (key: string): void {
        this.cache.delete(key)
    }

    /** Remove all entries. */
    public clear (): void {
        this.cache.clear()
    }

    /** Check if a non-expired entry exists for the key. */
    public has (key: string): boolean {
        return this.get(key) !== undefined
    }
}
